// import MadmaxCommanderBase from "../framework/src/madmax/MadmaxCommanderBase";

import {pixiSetTimeout} from "../framework/src/engine/pixi/pixiTimeout";
import Commander from "./Commander";

/**
 * Location: (framework)../src/Lights.js
 * Location: (game)src/Lights.js
 *
 * This class creates and animates the blinking lights around the reels.
 * Works on layers  lights and its sublayers base, one and two.
 *
 * @property commander {Commander}
 * @property layerBase {PIXI.Container} layer with Lights off sprites
 * @property layerOne {PIXI.Container} layer with Light shines that are initially on
 * @property layerTwo {PIXI.Container} layer with Light shines that are initially off
 * @property lights {array} references to all lights
 * @property left {array} references to lights on the left
 * @property right {array} references to lights on the right
 * @property tweens {array} array of tweens
 * @property timeouts {array} array of timeouts
 * @property state {string} current animation playing
 * @property tween {object} helps animate alpha of the 2 layers
 * @property toggleDuration {number} number in ms that repersent the duration of the animation
 * @property positions {array} array of coordinates of every light [[x,y],[x,y],....]
 * @class
 */
class Lights {
    constructor(commander) {
        this.commander = commander;
        this.layerBase = this.commander.layers.lights.children[0];
        this.layerOne = this.commander.layers.lights.children[1];
        this.layerTwo = this.commander.layers.lights.children[2];


        this.lights = [];
        this.left = [];
        this.right = [];
        this.tweens = [];
        this.timeouts = [];
        this.state = "";

        this.tween = {alpha: 0.0, alpha2: 0.0};
    }

    /**
     * Create lights, set their positions and start the animation.
     * See {@link Lights#animateLights}
     */
    create() {
        this.positions = [
            [932,65], [1227,70], // top 2 right
            [1254,120], [1254,197], [1254,265], // right side top 3
            [1254,435], [1254,505], [1254,575], // right side lower 3
            [1227,625], [932,624], [640,624], [350,624], [55,624], // bottom 5
            [27,575], [27,505], [27,435], // left side lower 3
            [27,265], [27,197], [27,120],  // left side top 3
            [55,70], [350,65] // top 2 left
        ];
        this.layerOne.alpha = 0;
        this.layerTwo.alpha = 1;

        this.toggleDuration = 1000; // ms
        this.initLights();
        this.setState("idle");

    }

    /**
     * Create every light inside a for loop. For each light, 4 sprites are created. First sprite is
     * the light when turned off and it is added to this.layerBase.
     * Other three sprites are lights when turned on. They are added to this.layerOne or this.layerTwo
     * according to their order. (even to layerOne, odd to layerTwo)
     * One layer represents the lights that are currently turned on, the other lights that are turned off.
     * When animating, alphas of both layers are changed.
     */
    initLights() {
        let i = 0;
        for (let pos of this.positions) {
            let lightContainer = new PIXI.Container();
            lightContainer.on = false;

            let lightOff = Lights.getLight(pos[0],pos[1]);
            let light_layer_1 = Lights.getLight(pos[0],pos[1],1);
            let light_layer_2 = Lights.getLight(pos[0],pos[1],2, 0.3);
            let light_layer_3 = Lights.getLight(pos[0],pos[1],3);

            lightContainer.addChild(light_layer_1);
            lightContainer.addChild(light_layer_2);
            lightContainer.addChild(light_layer_3);
            this.layerBase.addChild(lightOff);
            this.lights.push(lightContainer);

            if (i < 10) {
                this.right.push(lightContainer);
            } else if (i > 10) {
                this.left.unshift(lightContainer);
            }
            if((i++ % 2) === 0)
                this.layerOne.addChild(lightContainer);
            else
                this.layerTwo.addChild(lightContainer);
        }
    }

    /**
     * This method returns a promise that toggles the lights. It recursively calls itself so
     * the lights blink forever.
     */
    animateLights() {
        this.resetLights().then(() => {
            switch (this.state) {
                case "idle":
                    this.idleLights();
                    break;
                case "win":
                    this.winLights();
                    break;
                case "spin":
                    this.spinLights();
                    break;
            }
        });
    }

    /**
     * Slowly turn the lights off in between animations.
     * Turn off visibility while set all alphas to 1. Then change alpha of layer One and Two to 0.
     * Remove timeouts after spinLights and all tweens on layers and lights.
     * @return {Promise<any>}
     */
    resetLights() {
        return new Promise(res => {
            this.layerOne.visible = this.layerTwo.visible = false;

            for (let light of this.lights) {
                Lights.removeTween(light);
                Lights.setLightAlpha(light, 1);
            }

            this.clearTimeouts();
            this.clearTweens();

            this.tween.alpha = this.layerOne.alpha = 0;
            this.tween.alpha2 = this.layerTwo.alpha = 0;

            this.layerOne.visible = this.layerTwo.visible = true;
            Lights.removeTween(this.tween);
            createjs.Tween.get(this.tween)
                .to({alpha: 0, alpha2: 0}, 100)
                .call(() => {
                    for (let light of this.lights) {
                        Lights.setLightAlpha(light, 1);
                    }
                    Lights.removeTween(this.tween);
                    res();
                })
                .addEventListener("change", () => {
                    this.layerOne.alpha = this.tween.alpha;
                    this.layerTwo.alpha = this.tween.alpha2;
                });
        });
    }


    /**
     * Lights animation during idle state. Create a tween that loops in the background.
     * It changes alpha of both layers so that the lights are blinking in some order (every second light is on when others are off).
     */
    idleLights() {
        this.tween.alpha = 0;
        let i = false;
        this.clearTweens();
        createjs.Tween.get(this.tween, {loop: true})
            .to({alpha: 1.0}, this.toggleDuration, createjs.Ease.cubicOut)
            .wait(this.toggleDuration/2)
            .to({alpha: 0.0}, this.toggleDuration, createjs.Ease.cubicOut)
            .call(() => {i = !i;})
            .addEventListener("change", () => {
                if(i) {
                    this.layerOne.alpha = this.tween.alpha;
                } else {
                    this.layerTwo.alpha = this.tween.alpha;
                }
            });
    }

    /**
     * Also loops a tween in the background.
     * Blink all lights at once and faster than idle anim.
     */
    winLights() {
        this.tween.alpha = 0;
        createjs.Tween.get(this.tween, {loop: true})
            .to({alpha: 1.0}, this.toggleDuration/2, createjs.Ease.cubicOut)
            .to({alpha: 0.0}, this.toggleDuration/2, createjs.Ease.cubicIn)
            .addEventListener("change", () => {
                this.layerOne.alpha = this.tween.alpha;
                this.layerTwo.alpha = this.tween.alpha;
            });
    }

    /**
     * Recursively calls itself. Create timeouts that blink 1 light each. Timeouts are a little delayed and
     * each creates a tween that runs only once. Iterate through arrays this.right, this.left to select lights on
     * both sides. The lights in arrays are ordered from top to bottom.
     * Pass a callback to the last light blinked that ends one iteration of this animation. After the promise ends,
     * this method is called again to perform another iteration.
     */
    spinLights() {
        for(let light of this.lights) {
            Lights.setLightAlpha(light, 0);
        }
        new Promise(res => {
            this.layerOne.alpha = this.layerTwo.alpha = 1;
            let i = 0;
            for(let light of this.left) {
                this.timeouts.push(pixiSetTimeout(0.1*i++, () => { this.blinkLight(light)}));
            }
            i = 0;
            for(let light of this.right) {
                if (i === 9)
                    this.timeouts.push(pixiSetTimeout(0.1*i++, () => { this.blinkLight(light, res)}));
                else
                    this.timeouts.push(pixiSetTimeout(0.1*i++, () => { this.blinkLight(light)}));
            }
        }).then(() => {
            this.clearTimeouts();
            this.clearTweens();
            if (this.state === "spin") {
                this.spinLights();
            }
        });

    }

    /**
     * Create a tween that changes alpha of given light by calling {@link Lights#setLightAlpha}.
     * Call callback after the end, used in {@link Lights#spinLights}
     * @param light (PIXI.Container
     * @param callback
     */
    blinkLight(light, callback = () => {}) {
        if (this.state === "spin") {
            let tween = {alpha: 0.0};
            this.tweens.push(tween);

            createjs.Tween.get(tween)
                .to({alpha: 1.0}, this.toggleDuration / 5, createjs.Ease.linear)
                .to({alpha: 0.0}, this.toggleDuration / 5, createjs.Ease.linear)
                .call(() => {
                    Lights.removeTween(tween);
                    callback();
                })
                .addEventListener("change", () => {
                    Lights.setLightAlpha(light, tween.alpha);
                });
        } else {
            callback();
        }
    }

    clearTimeouts() {
        for(;this.timeouts.length > 0;)
            clearTimeout(this.timeouts.pop());
    }

    clearTweens() {
        for(;this.tweens.length > 0;)
            Lights.removeTween(this.tweens.pop());
    }


    /**
     * A light contains 3 parts. Change alpha value of all three to given number.
     * @param light {PIXI.Container}
     * @param value {number} new alpha value
     */
    static setLightAlpha(light, value) {
        light.children[0].alpha = value;
        light.children[1].alpha = 0.3*value;
        light.children[2].alpha = value;
    }

    /**
     * Changes the state. This method directly changes the animation by calling {@link Lights#animateLights}
     * If the new state is the same as the old state, do nothing.
     * @param state {string} can be one of the three ["idle"|"win"|"spin"]
     */
    setState(state) {
        if (this.state !== state) {
            this.state = state;
            this.animateLights();
        }
    }

    /**
     * Function creates sprite and returns it. The layer value defines the texture of the sprite.
     * -    0   -   reels_lights_off.png
     * -    1   -   reels_light_on_layer1.png
     * -    2   -   reels_light_on_layer2_overlay.png
     * -    3   -   reels_light_on_layer3.png
     * @param x {number} x position
     * @param y {number} y position
     * @param layer {number} texture of sprite
     * @param alpha {number} alpha value
     * @return {Sprite}
     */
    static getLight(x, y, layer = 0, alpha = 1) {
        let frame;
        switch(layer) {
            case 0:
                frame = "reels_light_off.png";
                break;
            case 1:
                frame = "reels_light_on_layer1.png";
                break;
            case 2:
                frame = "reels_light_on_layer2_overlay.png";
                break;
            case 3:
                frame = "reels_light_on_layer3.png";
                break;
        }
        let light = Commander.getSprite(frame, x, y);
        light.alpha = alpha;
        return light;
    }

    /**
     * Removes all tweens from given sprite.
     * @param sprite
     */
    static removeTween(sprite) {
        createjs.Tween.removeTweens(sprite);
    }
}
export default Lights;